import { useEffect, useState } from 'react';

import {
  getLatestVersion,
  getVersionsNewestFirst,
  type ChangelogEntry,
  type Version,
} from '../services/changelog';
import { readLastSeenVersion, writeLastSeenVersion } from '../services/last-seen-version';
import { compareSemver } from '../services/semver';

/**
 * Determine whether a version should be marked "new" relative to the version
 * the user had last seen on arrival.
 *
 * On first visit (`seenAtArrival == null`) no version is marked new, per the
 * first-visit rule (Req 6.6). Otherwise a version is new when it is strictly
 * greater than the last-seen version by semantic version ordering (Req 6.3).
 */
function isNewVersion(version: string, seenAtArrival: string | null): boolean {
  if (seenAtArrival == null) {
    return false;
  }
  return compareSemver(version, seenAtArrival) > 0;
}

/** Render a single changelog entry as a list item (Req 4.5, 8.1). */
function EntryItem({ entry }: { entry: ChangelogEntry }) {
  return (
    <li className="changelog-entry">
      {entry.scope ? <strong className="changelog-entry-scope">{entry.scope}:</strong> : null}{' '}
      <span className="changelog-entry-text">{entry.text}</span>
      {entry.commitUrl ? (
        <>
          {' '}
          <a
            className="changelog-commit-link"
            href={entry.commitUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            commit
          </a>
        </>
      ) : null}
    </li>
  );
}

/** Render one released version and its user-facing changes (Req 4.3–4.5, 8.1). */
function VersionSection({ version, isNew }: { version: Version; isNew: boolean }) {
  return (
    <section className="changelog-version">
      <h2 className="changelog-version-heading">
        <span className="changelog-version-number">v{version.version}</span>{' '}
        <span className="changelog-version-date">{version.date}</span>
        {isNew ? (
          <span className="changelog-new-badge">
            New <span className="sr-only">(new since your last visit)</span>
          </span>
        ) : null}
      </h2>

      {version.features.length > 0 ? (
        <>
          <h3 className="changelog-section-label">Features</h3>
          <ul className="changelog-entry-list">
            {version.features.map((entry, index) => (
              <EntryItem key={`feature-${index}`} entry={entry} />
            ))}
          </ul>
        </>
      ) : null}

      {version.fixes.length > 0 ? (
        <>
          <h3 className="changelog-section-label">Bug Fixes</h3>
          <ul className="changelog-entry-list">
            {version.fixes.map((entry, index) => (
              <EntryItem key={`fix-${index}`} entry={entry} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

/**
 * The Changelog page rendered at `/changelog`.
 *
 * Displays released versions newest-first with their user-facing changes
 * (Req 4). Versions newer than the version the user had last seen on arrival
 * are marked "new" with a non-colour textual indicator (Req 6.3, 8.5). On
 * mount the latest version is recorded as last-seen (Req 6.4) via a best-effort
 * write that never breaks rendering (Req 7.3, 7.4). An empty changelog renders
 * an empty-state message and no version content (Req 4.6).
 */
export function Changelog() {
  const versions = getVersionsNewestFirst();
  const latest = getLatestVersion();

  // Capture the last-seen version BEFORE recording, so "new" marking reflects
  // what the user hadn't seen on arrival. Lazy initialiser keeps this stable
  // across renders even after the on-mount write below.
  const [seenAtArrival] = useState<string | null>(() => readLastSeenVersion());

  // Record the latest version as last-seen on mount (Req 6.4). Best-effort:
  // the wrapper swallows errors, so a storage failure never breaks the page.
  useEffect(() => {
    if (latest != null) {
      writeLastSeenVersion(latest.version);
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="changelog">
      <h1 className="changelog-title">What&apos;s New</h1>

      {versions.length === 0 ? (
        <p className="changelog-empty">No changes to display yet.</p>
      ) : (
        versions.map((version) => (
          <VersionSection
            key={version.version}
            version={version}
            isNew={isNewVersion(version.version, seenAtArrival)}
          />
        ))
      )}
    </div>
  );
}
